import { Accordion, AccordionDetails, AccordionSummary, Button, Checkbox, FormControl, FormControlLabel, FormLabel, TextField, Typography } from "@mui/material";
import { ChevronRight } from "lucide-react";
import Badge from "../../components/ui/badge/Badge";
import { CRMS, EMAIL_MARKETING, FULFILMENT_TOOLS, HELPDESKS, REFUND_PLATFORMS, SUBSCRIPTION_APPS, type BrandStack, type OnboardingAnswers } from "../../types/onboarding";
import { CRM_LABELS, EMAIL_MARKETING_LABELS, FULFILMENT_LABELS, HELPDESK_LABELS, REFUND_PLATFORM_LABELS, SUBSCRIPTION_APP_LABELS } from "./labels";
import { SwitchCard } from "./ServicesSection";
import { findStackBrandIndexes, listNotSure, toStackSummary } from "./stack";
import { toggle } from "./toggle";

const EXCLUSIVE = new Set(["none", "not_sure"]);

// None and Not sure stand alone: picking one clears the rest, picking anything else clears them.
const toggleChoice = <T extends string>(list: T[], value: T): T[] =>
  EXCLUSIVE.has(value) ? (list.includes(value) ? [] : [value]) : toggle(list.filter((v) => !EXCLUSIVE.has(v)), value);

function StackLists({ stack, alertsOn, representment, isMissing, onChange }: {
  stack: BrandStack;
  alertsOn: boolean;
  representment: boolean;
  isMissing: (field: keyof BrandStack) => boolean;
  onChange: (patch: Partial<BrandStack>) => void;
}) {
  // Alerts need a platform to refund in, so None is only offered while they are off.
  const refundPlatforms = REFUND_PLATFORMS.filter((r) => r !== "none" || !alertsOn);
  const fulfilment = stack.fulfilment ?? [];
  const emailMarketing = stack.emailMarketing ?? [];

  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
      <FormControl error={isMissing("crms")}>
        <FormLabel>CRMs</FormLabel>
        {CRMS.map((c) => (
          <FormControlLabel key={c} label={CRM_LABELS[c]} control={<Checkbox checked={stack.crms.includes(c)} onChange={() => onChange({ crms: toggleChoice(stack.crms, c) })} />} />
        ))}
        {stack.crms.includes("other") && (
          <TextField label="Other CRM" value={stack.otherCrmName ?? ""} size="small" error={isMissing("otherCrmName")} onChange={(e) => onChange({ otherCrmName: e.target.value })} />
        )}
      </FormControl>
      <FormControl error={isMissing("refundsIn")}>
        <FormLabel>Refunds issued in</FormLabel>
        {refundPlatforms.map((r) => (
          <FormControlLabel
            key={r} label={REFUND_PLATFORM_LABELS[r]}
            control={<Checkbox checked={stack.refundsIn.includes(r)} onChange={() => onChange({ refundsIn: toggleChoice(stack.refundsIn, r) })} slotProps={{ input: { "aria-label": `Refunds in ${REFUND_PLATFORM_LABELS[r]}` } }} />}
          />
        ))}
        {stack.refundsIn.includes("other") && (
          <TextField
            label="Where do they refund?" placeholder="e.g. Stripe" value={stack.refundsOtherName ?? ""} size="small" error={isMissing("refundsOtherName")}
            onChange={(e) => onChange({ refundsOtherName: e.target.value })}
          />
        )}
      </FormControl>
      <FormControl error={isMissing("subscriptionApps")}>
        <FormLabel>Subscription apps</FormLabel>
        {SUBSCRIPTION_APPS.map((app) => (
          <FormControlLabel
            key={app} label={SUBSCRIPTION_APP_LABELS[app]}
            control={<Checkbox checked={stack.subscriptionApps.includes(app)} onChange={() => onChange({ subscriptionApps: toggleChoice(stack.subscriptionApps, app) })} slotProps={{ input: { "aria-label": `Subscription app ${SUBSCRIPTION_APP_LABELS[app]}` } }} />}
          />
        ))}
        {stack.subscriptionApps.includes("other") && (
          <TextField label="Other subscription app" value={stack.otherSubscriptionAppName ?? ""} size="small" error={isMissing("otherSubscriptionAppName")} onChange={(e) => onChange({ otherSubscriptionAppName: e.target.value })} />
        )}
      </FormControl>
      {representment && (
        <>
          <div className="col-span-full border-t border-gray-100 pt-3 dark:border-gray-800">
            <Typography variant="caption" color="text.secondary">Representment tools</Typography>
          </div>
          <FormControl error={isMissing("helpdesks")}>
            <FormLabel>Helpdesk</FormLabel>
            {HELPDESKS.map((h) => (
              <FormControlLabel
                key={h} label={HELPDESK_LABELS[h]}
                control={<Checkbox checked={stack.helpdesks.includes(h)} onChange={() => onChange({ helpdesks: toggleChoice(stack.helpdesks, h) })} slotProps={{ input: { "aria-label": `Helpdesk ${HELPDESK_LABELS[h]}` } }} />}
              />
            ))}
            {stack.helpdesks.includes("other") && (
              <TextField label="Other helpdesk" value={stack.otherHelpdeskName ?? ""} size="small" error={isMissing("otherHelpdeskName")} onChange={(e) => onChange({ otherHelpdeskName: e.target.value })} />
            )}
          </FormControl>
          <FormControl error={isMissing("fulfilment")}>
            <FormLabel>Fulfilment</FormLabel>
            {FULFILMENT_TOOLS.map((t) => (
              <FormControlLabel
                key={t} label={FULFILMENT_LABELS[t]}
                control={<Checkbox checked={fulfilment.includes(t)} onChange={() => onChange({ fulfilment: toggleChoice(fulfilment, t) })} slotProps={{ input: { "aria-label": `Fulfilment ${FULFILMENT_LABELS[t]}` } }} />}
              />
            ))}
            {fulfilment.includes("other") && (
              <TextField label="Other fulfilment tool" value={stack.otherFulfilmentName ?? ""} size="small" error={isMissing("otherFulfilmentName")} onChange={(e) => onChange({ otherFulfilmentName: e.target.value })} />
            )}
          </FormControl>
          <FormControl error={isMissing("emailMarketing")}>
            <FormLabel>Email marketing</FormLabel>
            {EMAIL_MARKETING.map((e) => (
              <FormControlLabel
                key={e} label={EMAIL_MARKETING_LABELS[e]}
                control={<Checkbox checked={emailMarketing.includes(e)} onChange={() => onChange({ emailMarketing: toggleChoice(emailMarketing, e) })} slotProps={{ input: { "aria-label": `Email marketing ${EMAIL_MARKETING_LABELS[e]}` } }} />}
              />
            ))}
            {emailMarketing.includes("other") && (
              <TextField label="Other email marketing" value={stack.otherEmailMarketingName ?? ""} size="small" error={isMissing("otherEmailMarketingName")} onChange={(e) => onChange({ otherEmailMarketingName: e.target.value })} />
            )}
          </FormControl>
        </>
      )}
    </div>
  );
}

export default function StackSection({ answers, set, missingFields }: {
  answers: OnboardingAnswers;
  set: (patch: Partial<OnboardingAnswers>) => void;
  missingFields: ReadonlySet<string>;
}) {
  const stackBrands = findStackBrandIndexes(answers.brands);
  const [first] = stackBrands;
  const brandName = (i: number) => answers.brands[i].name.trim() || `Brand ${i + 1}`;
  const setStack = (index: number, stack: BrandStack) =>
    set({ brands: answers.brands.map((brand, i) => (i === index ? { ...brand, stack } : brand)) });
  const lists = (i: number) => (
    <StackLists
      stack={answers.brands[i].stack} alertsOn={answers.alerts.enabled} representment={answers.representment}
      isMissing={(field) => missingFields.has(`brands.${i}.stack.${field}`)}
      onChange={(patch) => setStack(i, { ...answers.brands[i].stack, ...patch })}
    />
  );

  return (
    <>
      {stackBrands.length === 0 && (
        <Typography variant="body2" color="text.secondary">No brand in this onboarding gets steps, so there is no stack to answer.</Typography>
      )}
      {stackBrands.length === 1 && lists(first)}
      {stackBrands.length > 1 && (
        <div className="flex flex-col gap-3">
          {stackBrands.map((i, n) => {
            const fields = `brands.${i}.stack.`;
            const shown = [...missingFields].filter((f) => f.startsWith(fields)).length;
            const summary = toStackSummary(answers.brands[i].stack, answers.representment);
            const toConfirm = listNotSure(answers.brands[i].stack, answers.representment).length;
            return (
              <Accordion
                key={answers.brands[i].brandId ?? `new-${i}`} defaultExpanded={n === 0} disableGutters elevation={0} square
                slotProps={{ transition: { unmountOnExit: true } }}
                sx={{
                  border: "1px solid var(--color-gray-200)", borderRadius: "10px", overflow: "hidden", bgcolor: "transparent",
                  "&::before": { display: "none" }, ".dark &": { borderColor: "var(--color-gray-800)" },
                }}
              >
                <AccordionSummary
                  id={`stack-brand-${i}`} aria-controls={`stack-brand-${i}-lists`} expandIcon={<ChevronRight size={16} />}
                  sx={{
                    minHeight: 48, px: 2, gap: 1.5, flexDirection: "row-reverse",
                    "& .MuiAccordionSummary-content": { m: 0, minWidth: 0 },
                    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": { transform: "rotate(90deg)" },
                    "&.Mui-expanded": { bgcolor: "var(--color-gray-50)", borderBottom: "1px solid var(--color-gray-200)" },
                    ".dark &.Mui-expanded": { bgcolor: "rgba(255, 255, 255, 0.03)", borderColor: "var(--color-gray-800)" },
                  }}
                >
                  <span className="flex min-w-0 flex-1 items-center gap-3">
                    <span className="flex-none text-sm font-semibold text-gray-800 dark:text-white/90">{brandName(i)}</span>
                    <span className={`min-w-0 flex-1 truncate text-sm ${summary ? "text-gray-500" : "text-gray-400"}`}>{summary || "Not answered yet"}</span>
                    {shown > 0 && <Badge size="sm" color="error">{shown} missing</Badge>}
                    {toConfirm > 0 && <Badge size="sm" color="warning">{toConfirm} to confirm</Badge>}
                  </span>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                  {n > 0 && (
                    <Button size="small" sx={{ alignSelf: "flex-end" }} onClick={() => setStack(i, answers.brands[first].stack)}>
                      Copy from {brandName(first)}
                    </Button>
                  )}
                  {lists(i)}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </div>
      )}
      <SwitchCard
        title="Switching from another provider" hint="Enrolments become transfers."
        checked={answers.switching.enabled} onChange={(enabled) => set({ switching: { ...answers.switching, enabled } })}
      >
        <div className="flex flex-wrap items-center gap-4">
          <TextField
            label="Previous provider" value={answers.switching.provider ?? ""} size="small" sx={{ width: 260 }} error={missingFields.has("switching.provider")}
            onChange={(e) => set({ switching: { ...answers.switching, provider: e.target.value } })}
          />
          <FormControlLabel
            label="Some MIDs or descriptors were never with them"
            control={<Checkbox checked={answers.switching.someMidsNew} onChange={(e) => set({ switching: { ...answers.switching, someMidsNew: e.target.checked } })} />}
          />
        </div>
      </SwitchCard>
    </>
  );
}
